/**
 * Classify getUserMedia / call-media failures so UI does not treat every
 * failure as a browser permission denial (common iOS AudioSession footgun).
 */

export const CALL_MEDIA_ERROR_KIND = {
  PERMISSION: 'permission',
  AUDIO_SESSION: 'audio_session',
  DEVICE: 'device',
  UNSUPPORTED: 'unsupported',
  UNKNOWN: 'unknown',
};

/**
 * @param {unknown} err
 * @returns {{
 *   kind: string,
 *   name: string,
 *   message: string,
 *   audioSessionType: string | null,
 * }}
 */
export function classifyCallMediaError(err) {
  const name = String(err?.name || '');
  const message = String(err?.message || err?.msg || 'Media capture failed');
  const lower = message.toLowerCase();

  let audioSessionType = null;
  try {
    audioSessionType = navigator?.audioSession?.type ?? null;
  } catch (_) {
    audioSessionType = null;
  }

  const isAudioSessionConflict =
    name === 'InvalidStateError' ||
    lower.includes('audiosession') ||
    lower.includes('audio session') ||
    lower.includes('not compatible with audio capture');

  if (isAudioSessionConflict) {
    return {
      kind: CALL_MEDIA_ERROR_KIND.AUDIO_SESSION,
      name: name || 'InvalidStateError',
      message,
      audioSessionType,
    };
  }

  if (
    name === 'NotAllowedError' ||
    name === 'PermissionDeniedError' ||
    lower.includes('permission') ||
    lower.includes('not allowed')
  ) {
    return {
      kind: CALL_MEDIA_ERROR_KIND.PERMISSION,
      name: name || 'NotAllowedError',
      message,
      audioSessionType,
    };
  }

  if (
    name === 'NotFoundError' ||
    name === 'DevicesNotFoundError' ||
    name === 'OverconstrainedError' ||
    name === 'NotReadableError' ||
    name === 'TrackStartError' ||
    lower.includes('could not start') ||
    lower.includes('device in use')
  ) {
    return {
      kind: CALL_MEDIA_ERROR_KIND.DEVICE,
      name: name || 'NotFoundError',
      message,
      audioSessionType,
    };
  }

  if (
    name === 'TypeError' ||
    lower.includes('getusermedia') && lower.includes('undefined')
  ) {
    return {
      kind: CALL_MEDIA_ERROR_KIND.UNSUPPORTED,
      name: name || 'TypeError',
      message,
      audioSessionType,
    };
  }

  return {
    kind: CALL_MEDIA_ERROR_KIND.UNKNOWN,
    name: name || 'Error',
    message,
    audioSessionType,
  };
}

/**
 * Payload shape used by CallManager.callError / CallErrorState.
 * @param {unknown} err
 * @param {string} [fallbackCode]
 */
export function buildCallMediaAccessError(err, fallbackCode = 'no_user_media') {
  const classified = classifyCallMediaError(err);
  return {
    code: fallbackCode,
    message: classified.message,
    mediaKind: classified.kind,
    mediaName: classified.name,
    audioSessionType: classified.audioSessionType,
  };
}

export function getCallMediaErrorPresentation(error) {
  const kind = error?.mediaKind || CALL_MEDIA_ERROR_KIND.PERMISSION;

  switch (kind) {
    case CALL_MEDIA_ERROR_KIND.AUDIO_SESSION:
      return {
        variant: 'retry',
        title: 'Couldn’t start microphone',
        body: 'Audio was busy on this device. Tap Try again to rejoin with camera and mic.',
        primaryLabel: 'Try again',
      };
    case CALL_MEDIA_ERROR_KIND.DEVICE:
      return {
        variant: 'retry',
        title: 'Camera or mic unavailable',
        body: 'Another app may be using your camera or microphone. Close it, then try again.',
        primaryLabel: 'Try again',
      };
    case CALL_MEDIA_ERROR_KIND.UNSUPPORTED:
      return {
        variant: 'simple',
        message: 'This browser does not support calling. Try Safari or Chrome.',
      };
    case CALL_MEDIA_ERROR_KIND.PERMISSION:
    default:
      return {
        variant: 'permission',
        title: 'Enable Camera & Mic',
        body: 'To start a call, allow camera and microphone access when your browser asks.',
        primaryLabel: 'Allow access',
      };
  }
}
